const VERSION_PATTERN = /^(\d+)(?:\.(\d+))?(?:\.(\d+))?(?:-([^+]+))?(?:\+.*)?$/;

type ParsedLearnVersion = {
  release: number[];
  prerelease: string[] | null;
};

const compareStrings = (left: string, right: string): number => {
  if (left < right) return -1;
  if (left > right) return 1;
  return 0;
};

export const parseLearnVersion = (version: string): ParsedLearnVersion | null => {
  const match = VERSION_PATTERN.exec(version.trim());
  if (!match) return null;

  return {
    release: [match[1], match[2], match[3]].map((part) => Number(part ?? 0)),
    prerelease: match[4] ? match[4].split('.') : null,
  };
};

const compareIdentifiers = (left: string, right: string): number => {
  const leftNumeric = /^\d+$/.test(left);
  const rightNumeric = /^\d+$/.test(right);

  if (leftNumeric && rightNumeric) return Number(left) - Number(right);
  if (leftNumeric) return -1;
  if (rightNumeric) return 1;
  return compareStrings(left, right);
};

const comparePrerelease = (left: string[] | null, right: string[] | null): number => {
  if (!left && !right) return 0;
  if (!left) return 1;
  if (!right) return -1;

  const parts = Math.max(left.length, right.length);
  for (let index = 0; index < parts; index += 1) {
    if (index >= left.length) return -1;
    if (index >= right.length) return 1;

    const difference = compareIdentifiers(left[index], right[index]);
    if (difference !== 0) return difference;
  }
  return 0;
};

export const compareLearnVersions = (left: string, right: string): number => {
  const parsedLeft = parseLearnVersion(left);
  const parsedRight = parseLearnVersion(right);

  if (!parsedLeft && !parsedRight) return compareStrings(left, right);
  if (!parsedLeft) return -1;
  if (!parsedRight) return 1;

  for (let index = 0; index < 3; index += 1) {
    const difference = parsedLeft.release[index] - parsedRight.release[index];
    if (difference !== 0) return difference < 0 ? -1 : 1;
  }

  return comparePrerelease(parsedLeft.prerelease, parsedRight.prerelease);
};

export const isOlderLearnVersion = (candidate: string, current: string): boolean =>
  compareLearnVersions(candidate, current) < 0;
